import React, { useCallback, useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Infinity as InfinityIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CAP = 100;

// Admin: grant / revoke free unlimited access (capped at 100 summaries per month).
export function AdminUnlimited() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/unlimited-users");
      setUsers(data);
    } catch {
      setUsers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setAccess = async (target, enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/unlimited", { email: target.trim(), enabled });
      toast.success(data.message);
      if (enabled) setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la mise à jour.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#002FA7] p-6 max-w-3xl mt-6" data-testid="admin-unlimited-panel">
      <div className="sux-marker mb-2 flex items-center gap-2">
        <InfinityIcon className="w-3 h-3" /> Accès offert
        <InfoTip testid="admin-unlimited-info" content={"Le compte résume sans payer, jusqu'à 100 résumés par mois. Au-delà, il utilise ses crédits normaux. Le coût IA est pour vous."} />
      </div>
      <h3 className="text-xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
        Accès illimité
      </h3>
      <form onSubmit={(e) => { e.preventDefault(); setAccess(email, true); }} className="flex flex-wrap gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="collegue@exemple.fr"
          className="flex-1 min-w-[220px] border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#002FA7] bg-background"
          data-testid="admin-unlimited-email-input"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-unlimited-submit-btn"
          className="font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <InfinityIcon className="w-3 h-3" />} Activer
        </button>
      </form>
      <p className="mt-3 text-xs text-muted-foreground font-mono">
        Aucun crédit débité, plafond de sécurité : {CAP} résumés par mois (ensuite, crédits normaux). Payé par votre clé Universelle.
      </p>
      <ul className="mt-4 divide-y divide-border border-t border-border" data-testid="admin-unlimited-list">
        {users.length === 0 && (
          <li className="py-3 text-xs text-muted-foreground font-mono" data-testid="admin-unlimited-empty">Aucun compte illimité pour l&apos;instant.</li>
        )}
        {users.map((u) => (
          <li key={u.email} className="py-3 flex items-center gap-3 text-sm" data-testid={`admin-unlimited-row-${u.email}`}>
            <span className="font-mono flex-1 truncate">{u.email}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{u.used}/{CAP} ce mois</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => setAccess(u.email, false)}
              data-testid={`admin-unlimited-revoke-${u.email}`}
              className="font-mono uppercase tracking-[0.14em] text-[10px] border border-border px-3 py-1.5 hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Retirer
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AdminUnlimited;
